import { Supplier } from '../types/supplier';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3003/api';

export const supplierApiService = {
  /**
   * Fetch all suppliers from the backend
   */
  fetchSuppliers: async (): Promise<Supplier[]> => {
    try {
      const response = await fetch(`${API_BASE_URL}/suppliers`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to fetch suppliers');
      }

      return data.suppliers;
    } catch (error) {
      console.error('Error fetching suppliers from API:', error);
      throw error;
    }
  },

  /**
   * Fetch a single supplier by ID
   */
  fetchSupplierById: async (id: string): Promise<Supplier> => {
    try {
      const response = await fetch(`${API_BASE_URL}/suppliers/${id}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to fetch supplier');
      }

      return data.supplier;
    } catch (error) {
      console.error('Error fetching supplier from API:', error);
      throw error;
    }
  },

  /**
   * Create a new supplier
   */
  createSupplier: async (supplier: Supplier): Promise<Supplier> => {
    try {
      // Strip the client-generated ID before sending to backend
      const { id, ...supplierWithoutId } = supplier;

      const response = await fetch(`${API_BASE_URL}/suppliers`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(supplierWithoutId)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to create supplier');
      }

      return data.supplier;
    } catch (error) {
      console.error('Error creating supplier:', error);
      throw error;
    }
  },

  /**
   * Update an existing supplier
   */
  updateSupplier: async (supplier: Supplier): Promise<Supplier> => {
    try {
      const response = await fetch(`${API_BASE_URL}/suppliers/${supplier.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(supplier)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to update supplier');
      }

      return data.supplier;
    } catch (error) {
      console.error('Error updating supplier:', error);
      throw error;
    }
  },

  /**
   * Delete a supplier by ID
   */
  deleteSupplier: async (id: string): Promise<void> => {
    try {
      const response = await fetch(`${API_BASE_URL}/suppliers/${id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to delete supplier');
      }
    } catch (error) {
      console.error('Error deleting supplier:', error);
      throw error;
    }
  },

  /**
   * Delete all suppliers (for testing/admin)
   */
  deleteAllSuppliers: async (): Promise<void> => {
    try {
      const response = await fetch(`${API_BASE_URL}/suppliers`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to delete all suppliers');
      }
    } catch (error) {
      console.error('Error deleting all suppliers:', error);
      throw error;
    }
  }
};
